/**
 * Offline/demo fallback: when the backend is unreachable, API calls resolve
 * with dummy data instead of failing. A real HTTP error (4xx/5xx) is NOT
 * treated as offline — only network errors / timeouts (no response) are.
 *
 * Production NEVER falls back: a backend outage there must surface as a real
 * error rather than fabricated products and an accepted login.
 */
import { env } from "@/config/env";

/** True when the request never reached a responding backend. */
export const isOffline = (error) =>
  !error?.response &&
  (error?.code === "ERR_NETWORK" ||
    error?.code === "ECONNABORTED" ||
    error?.message === "Network Error" ||
    error?.request != null);

/** Run a request; on offline error, return the fallback instead. */
export const withFallback = async (request, fallback) => {
  try {
    return await request();
  } catch (error) {
    if (!env.IS_PROD && isOffline(error)) {
      return typeof fallback === "function" ? fallback() : fallback;
    }
    throw error;
  }
};

const matches = (item, filters = {}) => {
  if (!filters) return true;
  if (filters.section?.$eq && item.section !== filters.section.$eq) return false;
  if (filters.category?.$eq && item.category !== filters.category.$eq) return false;
  if (
    filters.title?.$containsi &&
    !(item.title ?? "").toLowerCase().includes(String(filters.title.$containsi).toLowerCase())
  ) {
    return false;
  }
  return true;
};

/** Build a mock list response { items, pagination } honoring basic filters. */
export const mockList = (items, params = {}) => {
  const filtered = items.filter((i) => matches(i, params.filters));
  return {
    items: filtered,
    pagination: {
      page: 1,
      pageSize: filtered.length,
      pageCount: 1,
      total: filtered.length,
    },
  };
};

/** Find one mock item by documentId (falls back to the first). */
export const mockOne = (items, documentId) =>
  items.find((i) => i.documentId === documentId) ?? items[0] ?? null;
